import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { EventBus } from '../core/EventBus';
import type { TravelConfig } from '../config/TravelConfig';
import type { FleetConfig } from '../config/FleetConfig';
import { SHIP_TYPE_DEFS, SHIP_TYPE_COUNT } from '../config/ShipTypes';
import { TravelPhase } from '../systems/TravelTypes';

/**
 * Expandable settings panel for travel parameters.
 * Mirrors the prototype's TravelControls — all values adjustable at runtime.
 * Communicates exclusively through EventBus.
 */
export class TravelSettingsPanel implements GameModule {
  readonly name = 'TravelSettingsPanel';

  private _events!: EventBus;
  private _config: TravelConfig;
  private _fleetConfig: FleetConfig;
  private _root!: HTMLDivElement;
  private _visible = false;

  constructor(config: TravelConfig, fleetConfig: FleetConfig) {
    this._config = config;
    this._fleetConfig = fleetConfig;
  }

  init(engine: Engine): void {
    this._events = engine.events;
    this._buildDOM();

    this._events.on<{ phase: TravelPhase }>('travel:phase-changed', (d) => {
      const idle = d.phase === TravelPhase.Idle || d.phase === TravelPhase.Arrived;
      if (!idle && this._visible) this._toggle();
    });
    this._events.on<null>('travel:toggle-settings', () => this._toggle());
  }

  update(_dt: number): void {}
  dispose(): void { this._root.remove(); }

  private _toggle(): void {
    this._visible = !this._visible;
    this._root.style.display = this._visible ? '' : 'none';
  }

  private _buildDOM(): void {
    this._root = document.createElement('div');
    Object.assign(this._root.style, {
      position: 'fixed', bottom: '52px', left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(10,12,20,0.92)', color: '#bbb', fontFamily: 'monospace',
      fontSize: 'clamp(10px,2.4vw,12px)', padding: 'clamp(10px,2vw,12px) clamp(12px,2.5vw,16px)',
      borderRadius: '8px',
      zIndex: '9997', display: 'none',
      border: '1px solid rgba(80,120,255,0.25)',
      maxWidth: '420px', width: '95vw',
    });

    this._addFleetSection();

    this._addSectionLabel('Normal');
    this._addNumberRow('Hop (s)', this._config.normal.hopDuration, 0.1, (v) => {
      this._config.normal.hopDuration = v;
      this._emit();
    });
    this._addNumberRow('Reveal ∅', this._config.normal.revealRadius, 1, (v) => {
      this._config.normal.revealRadius = v;
      this._emit();
    });

    this._addSectionLabel('Aufklärung');
    this._addNumberRow('Hop (s)', this._config.reveal.hopDuration, 0.1, (v) => {
      this._config.reveal.hopDuration = v;
      this._emit();
    });
    this._addNumberRow('Reveal ∅', this._config.reveal.revealRadius, 1, (v) => {
      this._config.reveal.revealRadius = v;
      this._emit();
    });

    this._addSectionLabel('Warp');
    this._addNumberRow('Warmup', this._config.warp.warmupHops, 1, (v) => {
      this._config.warp.warmupHops = v;
      this._emit();
    });
    this._addNumberRow('Warp-Hops', this._config.warp.warpHops, 1, (v) => {
      this._config.warp.warpHops = v;
      this._emit();
    });
    this._addNumberRow('Cooldown', this._config.warp.cooldownHops, 1, (v) => {
      this._config.warp.cooldownHops = v;
      this._emit();
    });
    this._addNumberRow('Warp-Hop (s)', this._config.warp.warpHopDuration, 0.1, (v) => {
      this._config.warp.warpHopDuration = v;
      this._emit();
    });
    this._addNumberRow('Normal-Hop (s)', this._config.warp.normalHopDuration, 0.1, (v) => {
      this._config.warp.normalHopDuration = v;
      this._emit();
    });
    this._addNumberRow('Min-Abstand', this._config.warp.minSafeDistance, 1, (v) => {
      this._config.warp.minSafeDistance = v;
      this._emit();
    });

    document.body.appendChild(this._root);
  }

  private _addFleetSection(): void {
    this._addSectionLabel('Flotte');
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const idx = i;
      const def = SHIP_TYPE_DEFS[idx];
      this._addNumberRow(def.name, this._fleetConfig.composition[idx], 1, (v) => {
        this._fleetConfig.composition[idx] = Math.max(0, Math.round(v));
        this._events.emit('fleet:composition-changed', null);
      });
    }
  }

  private _addSectionLabel(text: string): void {
    const el = document.createElement('div');
    el.textContent = text;
    Object.assign(el.style, {
      color: '#6af', fontWeight: 'bold', fontSize: '11px',
      marginTop: '8px', marginBottom: '2px', borderBottom: '1px solid #334',
      paddingBottom: '2px',
    });
    this._root.appendChild(el);
  }

  private _addNumberRow(label: string, value: number, step: number, onChange: (v: number) => void): void {
    const row = this._row();
    const lbl = document.createElement('span');
    lbl.textContent = label; lbl.style.flex = '1';
    const input = document.createElement('input');
    input.type = 'number'; input.value = String(value); input.step = String(step);
    this._styleInput(input);
    input.style.width = '70px';
    input.addEventListener('change', () => {
      const v = parseFloat(input.value);
      if (!isNaN(v) && v >= 0) onChange(v);
    });
    row.appendChild(lbl);
    row.appendChild(input);
    this._root.appendChild(row);
  }

  private _row(): HTMLDivElement {
    const r = document.createElement('div');
    Object.assign(r.style, { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' });
    return r;
  }

  private _styleInput(el: HTMLElement): void {
    Object.assign(el.style, {
      background: '#1a1e2e', color: '#ccc', border: '1px solid #444',
      borderRadius: '3px', padding: '3px 6px', fontSize: 'clamp(10px,2.4vw,12px)',
      fontFamily: 'monospace', minHeight: '44px',
    });
  }

  private _emit(): void {
    this._events.emit('travel:settings-changed', null);
  }
}
